import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { NavItem } from '@/layouts/PortalLayout'

export type NavBadgeKey = 'issues' | 'pending'
export type NavBadges = Partial<Record<NavBadgeKey, number>>

/**
 * Live counts for the sidebar badges, keyed by `NavItem.badge`. Each query
 * only runs when the menu actually asks for that badge, so the staff and
 * partner portals never call the admin endpoints.
 */
export function useNavBadges(nav: NavItem[]): NavBadges {
  const wantsIssues = nav.some((item) => item.badge === 'issues')
  // Pending sits on Orders in both the admin and the sales console; the
  // item's own path says which API to ask.
  const pendingItem = nav.find((item) => item.badge === 'pending')
  const pendingBase = pendingItem?.to.startsWith('/sales') ? '/sales' : '/admin'

  const issues = useQuery({
    queryKey: ['admin-issue-count'],
    queryFn: async () => (await api.get<{ open: number }>('/admin/issues/count')).data,
    enabled: wantsIssues,
    // Same cadence as the notification bell, and paused in a background tab.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })

  // Orders waiting in the office for Proceed.
  const pending = useQuery({
    queryKey: ['pending-count', pendingBase],
    queryFn: async () => (await api.get<{ pending: number }>(`${pendingBase}/orders/pending-count`)).data,
    enabled: !!pendingItem,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })

  return { issues: issues.data?.open, pending: pending.data?.pending }
}
